/* rs-grid-video — attaches and plays the film tiles that sit among the product cards.
   Paired with snippets/rs-grid-video-tile.liquid (2026-10-15).

   Three jobs, in order of how much they matter:
   1. Spend nothing until the tile is wanted. No src in the markup; the source is attached when the
      tile is near the viewport AND the page has finished loading, so the grid's own card images
      (the PLP's LCP) are never raced for bandwidth.
   2. Re-arm after a filter. The grid re-renders through the Section Rendering API, which replaces
      the DOM wholesale. init() is idempotent and runs again on every re-render, and tiles already
      wired carry data-rs-gv-ready so they are never double-bound.
   3. Give the CPU back. A tile that scrolls away pauses; muted autoplay resumes when it returns.
      A viewer who turned the sound ON keeps it: the tile still pauses off-screen (a narrated clip
      talking from below the fold is worse than silence — Petunia vertical tile, 2026-10-26) and
      resumes, still unmuted, when it comes back. The sound choice itself is never undone by scrolling.
   4. Survive a dead rendition (2026-10-27, audit IAR-001). f_auto:video is resolved per User-Agent on
      Cloudinary's side -- hvc1 mp4 for Safari, vp9 webm for Chrome/Firefox -- and a branch can sit
      unfinished for days: the Petunia vp9 derivative answered 423 "Processing continued in the
      background" for 26+ hours, so every Android Chrome / Firefox visitor got a poster with a dead
      play/speaker pair (error.code 4, readyState 0). On 'error' the tile swaps ONCE to the h264 mp4 at
      the identical q_auto:eco,w_<n>,du_<n> string, which strict transformations already deliver. If
      that fails too the tile goes is-dead: controls hidden, poster left standing, no false affordance. */
(function () {
  if (window.__rsGridVideo) return;
  window.__rsGridVideo = true;

  var afterLoadQueue = [];
  function afterLoad(fn) {
    if (document.readyState === 'complete') fn();
    else afterLoadQueue.push(fn);
  }
  window.addEventListener('load', function () {
    afterLoadQueue.splice(0).forEach(function (fn) { fn(); });
  }, { once: true });

  // Cloudinary rendition for a grid cell: a short, cheap, silent loop rather than the whole film.
  // Only rewrites an untransformed /video/upload/<version>/ URL, so a hand-tuned URL is left alone.
  function sized(url, seconds) {
    var marker = '/video/upload/';
    var i = url.indexOf(marker);
    if (i < 0) return url;
    var rest = url.slice(i + marker.length);
    if (!/^v[0-9]+\//.test(rest)) return url;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = dpr > 1 ? 800 : 600;
    var t = 'f_auto:video,q_auto:eco,w_' + w + (seconds ? ',du_' + seconds : '');
    return url.slice(0, i + marker.length) + t + '/' + rest;
  }

  // 2026-10-27 (IAR-001): the fallback keeps every other parameter byte-identical. The dccgnvdaw
  // cloud has STRICT TRANSFORMATIONS on for video too (the 2026-10-15 belief that video was
  // unrestricted was wrong): only the exact strings that already have a derived asset are served,
  // and f_mp4,vc_h264,q_auto:eco,w_<600|800>,du_<n> is one of them. Anything else is a 401.
  function h264(url) {
    return url.indexOf('f_auto:video') < 0 ? '' : url.replace('f_auto:video', 'f_mp4,vc_h264');
  }

  function wire(root) {
    if (root.hasAttribute('data-rs-gv-ready')) return;
    root.setAttribute('data-rs-gv-ready', '');
    var v = root.querySelector('.rs-gv__v');
    var sound = root.querySelector('.rs-gv__sound');
    var playBtn = root.querySelector('.rs-gv__play');
    if (!v) return;
    root.classList.add('is-muted');
    var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var attached = false;
    var userWantsSound = false;

    function play() {
      var p = v.play();
      if (p && p.catch) p.catch(function () {
        // Refused. An UNMUTED play() outside a tap (the observer's scroll-back resume, 2026-10-26)
        // is what iOS Safari refuses; fall back to muted autoplay and show the speaker as muted so
        // the viewer re-taps for sound. A refused MUTED play just leaves the poster standing.
        if (!v.muted) {
          v.muted = true; userWantsSound = false; root.classList.add('is-muted');
          var q = v.play(); if (q && q.catch) q.catch(function () {});
        }
      });
    }
    var current = '';         // the URL the element is on, so the fallback never re-reads a stale src
    function attach() {
      if (attached) return;
      attached = true;
      current = sized(v.getAttribute('data-src'), root.getAttribute('data-seconds'));
      v.src = current;
      v.load();
      if (!calm) play();
    }

    var userPaused = false;   // a deliberate Pause outranks the scroll observer
    var userPlayed = false;   // a deliberate Play under reduced-motion should survive the fallback too

    // 2026-10-27 (IAR-001): a rendition that will not load. One swap to the h264 mp4; a second
    // failure retires the controls (is-dead) so the poster is not dressed as a playable film.
    // Chromium reports a 423/octet-stream source as MEDIA_ERR_SRC_NOT_SUPPORTED, Firefox the same;
    // the event is on the element because the source is the src attribute, not a <source> child.
    var fellBack = false;
    v.addEventListener('error', function () {
      if (!attached) return;
      if (!fellBack) {
        fellBack = true;
        var alt = h264(current);
        if (alt) {
          current = alt;
          v.src = alt;
          v.load();
          if (!userPaused && (!calm || userWantsSound || userPlayed)) play();
          return;
        }
      }
      root.classList.add('is-dead');
      root.classList.remove('is-playing');
      if (playBtn) { playBtn.setAttribute('aria-pressed', 'false'); playBtn.setAttribute('aria-label', 'Play video'); playBtn.disabled = true; }
      if (sound) sound.disabled = true;
    });
    v.addEventListener('playing', function () {
      root.classList.add('is-playing');
      if (playBtn) { playBtn.setAttribute('aria-pressed', 'true'); playBtn.setAttribute('aria-label', 'Pause video'); }
    });
    v.addEventListener('pause', function () {
      root.classList.remove('is-playing');
      if (playBtn) { playBtn.setAttribute('aria-pressed', 'false'); playBtn.setAttribute('aria-label', 'Play video'); }
    });
    if (playBtn) {
      playBtn.addEventListener('click', function (e) {
        // The tile is wrapped in a link; this button is not a navigation.
        e.preventDefault();
        e.stopPropagation();
        attach();
        if (v.paused) { userPaused = false; userPlayed = true; play(); } else { userPaused = true; v.pause(); }
      });
    }

    if (sound) {
      sound.addEventListener('click', function (e) {
        // The tile is wrapped in a link; this button is not a navigation.
        e.preventDefault();
        e.stopPropagation();
        attach();
        v.muted = !v.muted;
        userWantsSound = !v.muted;
        root.classList.toggle('is-muted', v.muted);
        sound.setAttribute('aria-pressed', v.muted ? 'true' : 'false');
        sound.setAttribute('aria-label', v.muted ? 'Unmute' : 'Mute');
        if (!v.muted) play();
      });
    }

    if (!('IntersectionObserver' in window)) { afterLoad(attach); return; }

    var near = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { afterLoad(attach); near.disconnect(); }
      });
    }, { rootMargin: '400px 0px' });
    near.observe(root);

    // 2026-10-26: pause off-screen regardless of the sound choice (a narrated clip must not keep
    // talking from below the fold); on return, resume muted autoplay as before OR, if the viewer had
    // chosen sound, resume with sound — their choice survives the scroll, the noise does not.
    // Reduced-motion and a deliberate Pause still outrank the observer.
    new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) {
          if (!v.paused) v.pause();
        } else if (attached && v.paused && !userPaused && (userWantsSound || !calm)) {
          play();
        }
      });
    }, { threshold: 0.2 }).observe(root);
  }

  function init() {
    var tiles = document.querySelectorAll('[data-rs-gv]');
    for (var i = 0; i < tiles.length; i++) wire(tiles[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // The filter/sort grid swaps its HTML in without a page load; re-run for whatever arrived.
  document.addEventListener('shopify:section:load', init);
  var host = document.getElementById('AjaxContent') || document.body;
  if (window.MutationObserver) {
    new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) { init(); return; }
      }
    }).observe(host, { childList: true, subtree: true });
  }
})();
